import { observer } from 'mobx-react-lite';
import React, { FC } from 'react';
import { BoardContext } from '../../contexts/BoardContext';
import { useParams } from 'react-router-dom';
import { useFuncBoard } from '../../hooks/boardHooks/useFuncBoard';
import Loader from '../../component/Shared/Loader/Loader';

interface BoardProviderProps {
    children?: React.ReactNode;
}

const BoardProviderWrapper: FC<BoardProviderProps> = ({ children }) => {
    const { boardId } = useParams();
    const boardIdNumber = Number(boardId ?? undefined);
    
    // Hooks
    const boardTaskFunction = useFuncBoard(boardIdNumber);

    if (boardTaskFunction.isLoading) {
        return <Loader/>;
    }

    return (
        <BoardContext.Provider value={{
            // Data states
            boardIdNumber,
            accessRestrict: boardTaskFunction.accessRestrict,
            columns: boardTaskFunction.columns,
            filteredColumns: boardTaskFunction.filteredColumns,
            boardName: boardTaskFunction.boardName,
            projectId: boardTaskFunction.projectId,
            isProxyBoard: boardTaskFunction.isProxyBoard,
            abbreviationProject: boardTaskFunction.abbreviationProject,
            allStatusColumn: boardTaskFunction.allStatusColumn,
            projectSettings: boardTaskFunction.projectSettings,
            addTaskColumnId: boardTaskFunction.addTaskColumnId,
            
            // UI states
            actionMode: boardTaskFunction.actionMode,
            selectedTasks: boardTaskFunction.selectedTasks,
            selectedTasksToColumn: boardTaskFunction.selectedTasksToColumn,
            showTaskPopup: boardTaskFunction.showTaskPopup,
            showMenu: boardTaskFunction.showMenu,
            showConfirmDeleteTasks: boardTaskFunction.showConfirmDeleteTasks,
            showConfirmDeleteBoard: boardTaskFunction.showConfirmDeleteBoard,
            showConfirmDeleteColumn: boardTaskFunction.showConfirmDeleteColumn,
            
            // Filters
            taskNameSearch: boardTaskFunction.taskNameSearch,
            selectedFastFilters: boardTaskFunction.selectedFastFilters,
            boardFilters: boardTaskFunction.boardFilters,
            selectedBoardFilters: boardTaskFunction.selectedBoardFilters,
            filterCounter: boardTaskFunction.filterCounter,
            
            // Data
            relatedUsers: boardTaskFunction.relatedUsers,
            boardsForProxy: boardTaskFunction.boardsForProxy,
            colorOptions: boardTaskFunction.colorOptions,
            userAccess: boardTaskFunction.userAccess,
            systemAccess: boardTaskFunction.systemAccess,
            
            // Access flags
            editStatusAcess: boardTaskFunction.editStatusAcess,
            
            // Setter functions
            setTaskNameSearch: boardTaskFunction.setTaskNameSearch,
            setBoardFilters: boardTaskFunction.setBoardFilters,
            setSelectedBoardFilters: boardTaskFunction.setSelectedBoardFilters,
            setFilterCounter: boardTaskFunction.setFilterCounter,
            setShowTaskPopup: boardTaskFunction.setShowTaskPopup,
            setAddTaskColumnId: boardTaskFunction.setAddTaskColumnId,
            setShowConfirmDeleteTasks: boardTaskFunction.setShowConfirmDeleteTasks,
            setShowConfirmDeleteBoard: boardTaskFunction.setShowConfirmDeleteBoard,
            setShowConfirmDeleteColumn: boardTaskFunction.setShowConfirmDeleteColumn,
            
            // Action handlers
            handleDeleteTask: boardTaskFunction.handleDeleteTask,
            handleDeleteTasks: boardTaskFunction.handleDeleteTasks,
            handleSelectTask: boardTaskFunction.handleSelectTask,
            handleSelectTaskToColumn: boardTaskFunction.handleSelectTaskToColumn,
            handleSelectTasks: boardTaskFunction.handleSelectTasks,
            handleSelectTasksToColumn: boardTaskFunction.handleSelectTasksToColumn,
            handleMoveTask: boardTaskFunction.handleMoveTask,
            handleMoveTaskBoard: boardTaskFunction.handleMoveTaskBoard,
            handleMoveTasks: boardTaskFunction.handleMoveTasks,
            handleMoveColumn: boardTaskFunction.handleMoveColumn,
            handleMoveTasksOutsideBoard: boardTaskFunction.handleMoveTasksOutsideBoard,
            handleDeleteColumn: boardTaskFunction.handleDeleteColumn,
            handleEditColumn: boardTaskFunction.handleEditColumn,
            handleRenameBoard: boardTaskFunction.handleRenameBoard,
            handleDeleteBoard: boardTaskFunction.handleDeleteBoard,
            handleAddColumn: boardTaskFunction.handleAddColumn,
            handleMakeColumnCommon: boardTaskFunction.handleMakeColumnCommon,
            handleMakeColumnProxy: boardTaskFunction.handleMakeColumnProxy,
            handleTaskAction: boardTaskFunction.handleTaskAction,
            
            // Helper functions
            changeFastFilters: boardTaskFunction.changeFastFilters,
            cancelActionMode: boardTaskFunction.cancelActionMode,
            openMenu: boardTaskFunction.openMenu,
            closeMenu: boardTaskFunction.closeMenu,
            openAddTaskForm: boardTaskFunction.openAddTaskForm,
            resetFilter: boardTaskFunction.resetFilter,
            deleteTasks: boardTaskFunction.deleteTasks,
            deleteColumn: boardTaskFunction.deleteColumn,
            deleteBoard: boardTaskFunction.deleteBoard,
        }}>
            {children}
        </BoardContext.Provider>
    );
};

const BoardProvider: FC<BoardProviderProps> = ({ children }) => {
    const { boardId } = useParams();
    
    if (!boardId) {
        return <>{children}</>;
    }
    
    return (
        <BoardProviderWrapper>
            {children}
        </BoardProviderWrapper>
    );
};

export default observer(BoardProvider);